import groupBy from 'lodash/groupBy'
import React, { ChangeEvent, MutableRefObject, useEffect, useRef, useState } from 'react'

import { IconSearch } from '@avantstay/backoffice-vectors'

import { Checkbox } from '../../components/Checkbox'
import { FiltersSharedProps, OptionsProps } from '../../components/Filters/Filters.types'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import { getLabelsForSelectedOptions } from '../../utils/getLabelsForSelectedOptions'
import { useIsTabletOrMobile } from '../../utils/mediaQueries'
import { checkIfValidUUID } from '../../utils/strings'
import { DefaultLink } from '../Link'
import { Loading } from '../Loading'
import { SearchInput } from '../SearchInput'
import * as S from './CheckboxFilter.styles'
import { ClearButton, FilterSearchContainer } from './Filter.styles'
import { setCheckboxValue } from './Filter.utils'

const MIN_OPTION_COUNT_FOR_SCROLL = 11
const MIN_OPTION_COUNT_FOR_SEARCH_AND_SELECT_ALL = 7
const MIN_OPTION_COUNT_LONG_LIST = 5

enum ScrollPosition {
  Top,
  Between,
  Bottom,
}

interface CheckboxBasedFilterProps<OptionValue extends string> extends FiltersSharedProps {
  alwaysShowAll?: boolean
  autoFocusSearchInput?: boolean
  className?: string
  hideSelectAllOption?: boolean
  initialValue?: OptionValue[]
  isAccordionOpen?: boolean
  isLoadingOptions?: boolean
  isSearchEnabled?: boolean
  onChange: (value?: OptionValue[]) => void
  onClose?: VoidFunction
  options: OptionsProps<OptionValue>[]
  searchPlaceholder?: string
  shouldGroupBy?: boolean
  shouldShowClearButton?: boolean
  shouldUpdateOnInitialValueChange?: boolean
  title?: string
}

export function CheckboxBasedFilter<OptionValue extends string>({
  alwaysShowAll,
  autoFocusSearchInput,
  className,
  hideSelectAllOption,
  initialValue,
  isAccordionOpen,
  isLoadingOptions,
  isSearchEnabled = false,
  isSidebarFilter = false,
  onChange,
  onClose,
  onShowFilterAsColumn,
  options,
  searchPlaceholder,
  shouldGroupBy,
  shouldShowClearButton = true,
  shouldUpdateOnInitialValueChange = false,
  showFilterAsColumn,
  title,
}: CheckboxBasedFilterProps<OptionValue>) {
  const [selectedOptions, setSelectedOptions] = useState<OptionValue[] | undefined>(initialValue)
  const [searchFilterValue, setSearchFilterValue] = useState<string | undefined>('')
  const [scrollPosition, setScrollPosition] = useState(ScrollPosition.Top)
  const [showAll, setShowAll] = useState(false)

  const isMobile = useIsTabletOrMobile()

  const showSearchAndAll = options?.length >= MIN_OPTION_COUNT_FOR_SEARCH_AND_SELECT_ALL

  const clearScrollListenerRef = useRef<VoidFunction>()
  const groupIdRef = useRef(window.crypto.getRandomValues(new Uint8Array(1))[0])
  const wrapperRef: MutableRefObject<HTMLDivElement | null> = useRef(null)

  const handleSubmitValue = () => {
    if (selectedOptions !== initialValue) {
      onChange(selectedOptions)
    }
    onClose?.()
  }

  const wrapperCallbackRef = (element: HTMLDivElement | null) => {
    clearScrollListenerRef.current?.()
    clearScrollListenerRef.current = undefined

    wrapperRef.current = element

    if (!element) return

    const listener = () => {
      const { clientHeight, scrollHeight, scrollTop } = element

      const scrollTopValueForTop = 0
      if (scrollTop === scrollTopValueForTop) {
        setScrollPosition(ScrollPosition.Top)
        return
      }

      const scrollTopValueForBottom = scrollHeight - clientHeight
      if (scrollTop === scrollTopValueForBottom) {
        setScrollPosition(ScrollPosition.Bottom)
        return
      }

      setScrollPosition(ScrollPosition.Between)
    }

    listener()

    element.addEventListener('scroll', listener)
    clearScrollListenerRef.current = () => {
      element.removeEventListener('scroll', listener)
    }
  }

  useOnClickOutside(wrapperRef, handleSubmitValue)

  useEffect(() => {
    if (isSidebarFilter) {
      handleSubmitValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions, isSidebarFilter])

  // This clears selection when we run resetFilters
  useEffect(() => {
    if (initialValue === undefined) {
      setSelectedOptions(undefined)
    }
  }, [initialValue])

  useEffect(() => {
    if (shouldUpdateOnInitialValueChange) {
      setSelectedOptions(initialValue)
    }
  }, [initialValue, shouldUpdateOnInitialValueChange])

  useEffect(() => {
    return () => {
      clearScrollListenerRef.current?.()
    }
  }, [])

  const handleOnClear = () => {
    onChange(undefined)
    setSelectedOptions(undefined)
    onClose?.()
  }

  const handleCheckboxChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const updatedOptions = setCheckboxValue({
      selectedValues: selectedOptions || [],
      value: target.value as OptionValue,
    })
    setSelectedOptions(updatedOptions)
  }

  const optionsMatchingSearch = (() => {
    if (!options) {
      return []
    }

    if (!searchFilterValue) {
      return options
    }

    return options.filter(option => {
      const labelString = option.label?.toString() ?? option.value.toString()
      const searchLabelString = option.searchLabel?.toString()

      const regex = new RegExp(
        `(^| )${searchFilterValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
        'i',
      )

      return (
        (labelString && regex.test(labelString)) ||
        (searchLabelString && regex.test(searchLabelString))
      )
    })
  })()

  const hasOptionsMatchingSearch = optionsMatchingSearch.length > 0
  const isScrollable = !isSidebarFilter && options?.length >= MIN_OPTION_COUNT_FOR_SCROLL

  const getFilterValue = () => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return selectedOptions
    }

    if (checkIfValidUUID(selectedOptions[0])) {
      return String(selectedOptions.length)
    }

    return getLabelsForSelectedOptions(selectedOptions, options ?? [])
  }

  const listShortening =
    !alwaysShowAll &&
    isMobile &&
    !isAccordionOpen &&
    optionsMatchingSearch.length > MIN_OPTION_COUNT_LONG_LIST
  const hiddenItemsCount = optionsMatchingSearch.length - MIN_OPTION_COUNT_LONG_LIST

  const optionsToRender = optionsMatchingSearch.slice(
    listShortening ? 0 : undefined,
    listShortening && !showAll ? MIN_OPTION_COUNT_LONG_LIST : undefined,
  )

  const optionsGrouped = shouldGroupBy ? groupBy(optionsToRender, 'group') : []

  return (
    <S.Container
      className={className}
      filterValue={getFilterValue()}
      hasBottomShadow={isScrollable && scrollPosition !== ScrollPosition.Bottom}
      isAccordionOpen={isAccordionOpen}
      isScrollable={isScrollable}
      isSidebarFilter={isSidebarFilter}
      onClearFilter={handleOnClear}
      ref={wrapperCallbackRef}
      shouldDisplayFilterValueAsIs
      shouldShowClearButton={(shouldShowClearButton || isSidebarFilter) && !isScrollable}
      title={title}
      onShowFilterAsColumn={onShowFilterAsColumn}
      showFilterAsColumn={showFilterAsColumn}
    >
      {isLoadingOptions ? (
        <Loading size="4px" />
      ) : (
        <>
          {(isSearchEnabled || isScrollable || showSearchAndAll) && (
            <FilterSearchContainer
              hasBottomShadow={isScrollable && scrollPosition !== ScrollPosition.Top}
              isSticky={isScrollable}
            >
              <SearchInput
                autoFocus={autoFocusSearchInput}
                inputLeftIcon={<IconSearch width={16} height={16} />}
                onChange={filteredValue => setSearchFilterValue(filteredValue)}
                placeholder={searchPlaceholder}
              />
            </FilterSearchContainer>
          )}
          {hasOptionsMatchingSearch ? (
            <Checkbox.Group>
              {!hideSelectAllOption && (isScrollable || showSearchAndAll) && (
                <>
                  <Checkbox.Regular
                    checked={selectedOptions?.length === options.length}
                    counterActions={
                      isScrollable && !isSidebarFilter ? (
                        <ClearButton disabled={!selectedOptions?.length} onClick={handleOnClear}>
                          Clear
                        </ClearButton>
                      ) : undefined
                    }
                    hasFullWidth
                    id={`${groupIdRef.current}-all`}
                    onChange={event => {
                      setSelectedOptions(
                        event.target.checked ? options.map(option => option.value) : [],
                      )
                    }}
                    title="All"
                    value={`${groupIdRef.current}-all`}
                  />
                  <S.LineSeparator />
                </>
              )}
              {shouldGroupBy
                ? Object.entries(optionsGrouped).map(([group, list]) => {
                    return (
                      <>
                        <S.GroupLabel>{group}</S.GroupLabel>
                        {list.map(({ value, label, dot, hasFullWidth }) => (
                          <S.CheckboxRow
                            id={`${groupIdRef.current}_${value}`}
                            key={`${value}-${label}`}
                            checked={selectedOptions ? selectedOptions?.includes(value) : false}
                            onChange={handleCheckboxChange}
                            title={label}
                            value={value}
                            dot={dot}
                            hasFullWidth={hasFullWidth}
                          />
                        ))}
                      </>
                    )
                  })
                : optionsToRender.map(({ value, label, dot, hasFullWidth }) => (
                    <S.CheckboxRow
                      id={`${groupIdRef.current}_${value}`}
                      key={`${value}-${label}`}
                      checked={selectedOptions ? selectedOptions?.includes(value) : false}
                      onChange={handleCheckboxChange}
                      title={label || value}
                      value={value}
                      dot={dot}
                      hasFullWidth={hasFullWidth}
                    />
                  ))}
              {listShortening && (
                <DefaultLink onClick={() => setShowAll(all => !all)}>
                  {!showAll ? `Show more (${hiddenItemsCount})` : null}
                </DefaultLink>
              )}
            </Checkbox.Group>
          ) : (
            <S.EmptyResults>No results found</S.EmptyResults>
          )}
        </>
      )}
    </S.Container>
  )
}
